// @ts-strict-ignore
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  Input,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { uniqueId } from 'lodash-es';
import { KeyboardEventKey } from '../../../../model/enums/keyboard-event-keys';
import { Language } from '../../../../model/enums/language';
import { GlobalIcon, GlobalIconColour, GlobalIconSize } from '../icon/icon.constants';
import { InputBaseComponent } from '../input-base/input-base.component';

export interface DropdownOption<T> {
  stringId?: string;
  string?: string;
  value: T;
  disabled?: boolean;
}

interface IconConfig {
  icon?: GlobalIcon;
  size?: GlobalIconSize;
  margin?: number;
  colour?: GlobalIconColour;
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DropdownComponent<T> extends InputBaseComponent<T> {
  @Input() ariaLabel: string;
  @Input() comboboxIcon: IconConfig = { icon: GlobalIcon.CheckCircle, size: GlobalIconSize.Medium, margin: 12 };
  @Input() darkMode?: boolean;
  @Input() lang: Language;
  @Input() optionIcon: IconConfig = { icon: GlobalIcon.Check, size: GlobalIconSize.Small, margin: 6 };
  @Input() options: DropdownOption<T>[] = [];
  @Input() placeholder: string;
  @Input() selectedOption: T;
  @Input() selectedTextDisplay: string;
  @Input() visibleLabel: string;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const dropdownElement = this._document.getElementById(this.comboId);

    if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
      this.updateMenuState(false, false);
    }
  }

  activeIndex: number = -1;
  classes: string[] = [];
  comboId = uniqueId('combo-');
  isDisabled: boolean = false;
  labelId = uniqueId('dropdown-label-');
  listboxId = uniqueId('listbox-');
  open: boolean = false;
  selectedIndex: number = -1;
  selectedOptionIconColour: GlobalIconColour;

  private _ignoreBlur: boolean = false;

  get selectedOptionLabel(): string {
    return this.options[this.selectedIndex]?.stringId;
  }

  get selectedOptionLabelTranslated(): string {
    return this.options[this.selectedIndex]?.string;
  }

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    @Self() @Optional() public control: NgControl
  ) {
    super(control);
  }

  ngOnInit(): void {
    this.classes = [...(this.darkMode ? ['combo-dark'] : [])];
    if (!this.placeholder && !this.selectedOption) {
      this.activeIndex = 0;
      this.selectedIndex = 0;
      this.selectedOption = this.options[0]?.value;
    }

    this.optionIcon.colour = this.darkMode ? GlobalIconColour.White : GlobalIconColour.Dark;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedOption) {
      this.selectOption(this.options?.findIndex((o) => o.value === this.selectedOption));
    }
  }

  closeDropdown(): void {
    this.open = false;
  }

  getOptionId(index: number): string {
    return `${this.comboId}-${index}`;
  }

  onComboBlur(): void {
    // do not do blur action if ignoreBlur flag has been set
    if (this._ignoreBlur) {
      this._ignoreBlur = false;
      return;
    }

    // select current option (if active) and close
    if (this.open) {
      if (this.activeIndex >= 0) {
        this.selectOption(this.activeIndex);
      }
      this.updateMenuState(false, false);
    }
  }

  onComboClick(): void {
    this.updateMenuState(!this.open, false);
  }

  onComboKeyDown(event: KeyboardEvent): void {
    const { key } = event;
    switch (key) {
      case KeyboardEventKey.ArrowDown:
        event.preventDefault();
        if (!this.open) {
          this.updateMenuState(true);
          break;
        }
        this._activeIndexNext();
        this._checkResultVisibilityScroll(this.activeIndex);
        break;
      case KeyboardEventKey.ArrowUp:
        event.preventDefault();
        if (!this.open) {
          this.updateMenuState(true);
          break;
        }
        this._activeIndexPrevious();
        this._checkResultVisibilityScroll(this.activeIndex);
        break;
      case KeyboardEventKey.Space:
        if (!this.open) {
          this.updateMenuState(true);
        }
        break;
      case KeyboardEventKey.Enter:
        if (!this.open) {
          this.updateMenuState(true);
        } else if (this.activeIndex >= 0) {
          this.onOptionClick(this.activeIndex);
        }
        break;
      case KeyboardEventKey.Escape:
        if (this.open) {
          this.updateMenuState(false);
        }
        break;
    }
  }

  onOptionClick(index: number) {
    this.selectOption(index);
    this.updateMenuState(false);
    this._ignoreBlur = false;
  }

  onOptionMouseDown() {
    this._ignoreBlur = true;
  }

  selectOption(index: number): void {
    const changedValue = this.selectedOption !== this.options[index]?.value;
    this.activeIndex = index;
    this.selectedIndex = index;
    this.selectedOption = this.options[index]?.value;
    if (changedValue) {
      this.onChange(this.selectedOption);
    }
  }

  updateMenuState(open: boolean, callFocus: boolean = true): void {
    if (this.open === open) {
      return;
    }
    this.open = open;
  }

  private _activeIndexNext() {
    if (this.options.length - 1 > this.activeIndex) {
      this.activeIndex++;
    } else {
      this.activeIndex = 0;
    }
    if (this.options[this.activeIndex].disabled) {
      this._activeIndexNext();
    }
  }
  private _activeIndexPrevious() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    } else {
      this.activeIndex = this.options.length - 1;
    }
    if (this.options[this.activeIndex].disabled) {
      this._activeIndexPrevious();
    }
  }

  private _checkResultVisibilityScroll(index: number): void {
    const threshold = 1;
    const resultEl = this._document.getElementById(this.getOptionId(index));
    const listboxEl = this._document.getElementById(this.listboxId);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            let distToScroll = resultEl.offsetTop - resultEl.offsetHeight;
            listboxEl?.scrollTo({
              top: distToScroll,
              left: 0,
              behavior: 'smooth',
            });
          }
          observer.disconnect();
        });
      },
      { threshold }
    );
    observer.observe(resultEl);
  }
}
